/* import __COLOCATED_TEMPLATE__ from './map-user-objects-between-systems.hbs'; */
/* RESPONSIBLE TEAM: team-standalone */
import { inject as service } from '@ember/service';
import type FinStandaloneService from 'embercom/services/fin-standalone-service';
import type IntlService from 'ember-intl/services/intl';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { post } from 'embercom/lib/ajax';
import { action } from '@ember/object';

interface Args {}

export default class StandaloneSalesforceSetupMapUserObjectsBetweenSystems extends Component<Args> {
  @service declare finStandaloneService: FinStandaloneService;
  @service declare appService: $TSFixMe;
  @service declare intl: IntlService;
  @service declare notificationsService: $TSFixMe;

  @tracked selectedFinIdentifier: string;
  @tracked selectedSalesforceIdentifier: string;

  constructor(owner: unknown, args: Args) {
    super(owner, args);
    this.selectedFinIdentifier =
      this.finStandaloneService.salesforceConfig!.identityMappingUserAttribute;
    this.selectedSalesforceIdentifier =
      this.finStandaloneService.salesforceConfig!.identityMappingContactField;
  }

  get app() {
    return this.appService.app;
  }

  get finIdentifierOptions() {
    return [
      {
        text: this.intl.t(
          'standalone.salesforce.setup.map-user-objects-between-systems.fin-identifier-options.email',
        ),
        value: 'email',
      },
      {
        text: this.intl.t(
          'standalone.salesforce.setup.map-user-objects-between-systems.fin-identifier-options.custom-id',
        ),
        value: 'user_id',
      },
    ];
  }

  get selectedFinIdentifierText() {
    return this.finIdentifierOptions.find((option) => option.value === this.selectedFinIdentifier)
      ?.text;
  }

  get selectedSalesforceIdentifierText() {
    return this.salesforceIdentifierOptions.find(
      (option) => option.value === this.selectedSalesforceIdentifier,
    )?.text;
  }

  get salesforceIdentifierOptions() {
    return this.finStandaloneService.salesforceDataFields.userFields
      .filter((field) => field.isMappable && field.name !== 'Email')
      .map((field) => ({
        text: field.name,
        value: field.id,
      }));
  }

  get emailField() {
    return this.finStandaloneService.salesforceDataFields.userFields.find(
      (field) => field.name === 'Email',
    );
  }

  @action async onFinIdentifierSelect(value: string) {
    this.selectedFinIdentifier = value;

    if (value === 'email') {
      this.selectedSalesforceIdentifier = this.emailField?.id || '';
    } else if (this.selectedSalesforceIdentifier === this.emailField?.id) {
      this.selectedSalesforceIdentifier = this.salesforceIdentifierOptions[0]?.value || '';
    }

    this.saveIdentityMappingFields();
  }

  @action async onSalesforceIdentifierSelect(value: string) {
    this.selectedSalesforceIdentifier = value;
    this.saveIdentityMappingFields();
  }

  async saveIdentityMappingFields() {
    try {
      await post(
        `/ember/standalone/salesforce_configurations/${this.app.id}/update_identity_mapping_fields`,
        {
          identity_mapping_user_attribute: this.selectedFinIdentifier,
          identity_mapping_contact_field: this.selectedSalesforceIdentifier,
        },
      );

      this.finStandaloneService.salesforceConfig!.identityMappingUserAttribute = this
        .selectedFinIdentifier as 'user_id' | 'email';
      this.finStandaloneService.salesforceConfig!.identityMappingContactField =
        this.selectedSalesforceIdentifier;

      // replace this message by adding a busy state to dropdowns
      this.notificationsService.notifyConfirmation(
        this.intl.t('standalone.salesforce.setup.map-user-objects-between-systems.success'),
      );
    } catch (error) {
      this.notificationsService.notifyResponseError(error, { default: error.message });
    }
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Standalone::Salesforce::Setup::MapUserObjectsBetweenSystems': typeof StandaloneSalesforceSetupMapUserObjectsBetweenSystems;
    'standalone/salesforce/setup/map-user-objects-between-systems': typeof StandaloneSalesforceSetupMapUserObjectsBetweenSystems;
  }
}
